<template>
  <div class="dashboard-vendas">
    <!-- Barra de tabs só aparece quando há mais de uma tab (Gamificação depende do CRM) -->
    <div v-if="crmAtivo" class="dashboard-tabs-header">
      <button
        :class="['dashboard-tab-btn', { active: activeTab === 'vendas' }]"
        @click="activeTab = 'vendas'"
      >
        <feather-icon icon="TrendingUpIcon" size="16" />
        <span>Pedidos</span>
      </button>
      <button
        :class="['dashboard-tab-btn', { active: activeTab === 'gamificacao' }]"
        @click="activeTab = 'gamificacao'"
      >
        <feather-icon icon="AwardIcon" size="16" />
        <span>Gamificação</span>
      </button>
    </div>

    <gamification-dashboard v-if="crmAtivo && activeTab === 'gamificacao'" />

    <b-overlay v-else :show="isLoading" variant="light" blur="2px">
      <div class="page-header">
        <div class="header-top">
          <div>
            <p v-if="nomeUsuarioLogado" class="page-greeting">Olá, {{ nomeUsuarioLogado }}!</p>
            <h2 class="page-title">{{ ehRepresentante ? 'Dashboard de Doação (Equipe Vendas)' : 'Dashboard de Vendas' }}</h2>
            <p class="page-subtitle">
              Acompanhe seus pedidos{{ ehRepresentante ? ' de doação' : '' }}<span v-if="!ehRepresentante && $store.getters['preferencias/comissoesAtivo']">, comissões</span> e clientes
            </p>
          </div>
          <div class="filters-inline">
            <b-form-datepicker v-model="filters.dataInicio" placeholder="Data Início" size="sm" class="filter-date" />
            <b-form-datepicker v-model="filters.dataFim" placeholder="Data Fim" size="sm" class="filter-date" />
            <b-button variant="primary" size="sm" @click="loadData">
              <feather-icon icon="FilterIcon" size="14" /> Filtrar
            </b-button>
          </div>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><feather-icon icon="ShoppingBagIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Meus Pedidos</span>
            <span class="stat-value">{{ data.resumo.totalPedidos || 0 }}</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><feather-icon icon="DollarSignIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">{{ ehRepresentante ? 'Valor Total Doado' : 'Valor Total Vendido' }}</span>
            <span class="stat-value">{{ formatCurrency(data.resumo.valorTotalPedidos) }}</span>
          </div>
        </div>
        <div v-if="!ehRepresentante && $store.getters['preferencias/comissoesAtivo']" class="stat-card stat-warning">
          <div class="stat-icon"><feather-icon icon="AwardIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Comissão Acumulada</span>
            <span class="stat-value">{{ formatCurrency(data.comissaoTotal.totalComissao) }}</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><feather-icon icon="UsersIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Clientes Atendidos</span>
            <span class="stat-value">{{ data.resumo.totalClientes || 0 }}</span>
          </div>
        </div>
        <div
          class="stat-card stat-score"
          :style="{ borderLeft: '4px solid ' + scoreColor }"
          @click="showScoreModal = true"
          @mouseenter="startScoreHover"
          @mouseleave="cancelScoreHover"
        >
          <div class="score-gauge-mini">
            <svg viewBox="0 0 60 60" width="48" height="48">
              <circle cx="30" cy="30" r="24" fill="none" stroke="#e9ecef" stroke-width="5"
                :stroke-dasharray="miniCircumference" transform="rotate(-225 30 30)" />
              <circle cx="30" cy="30" r="24" fill="none" :stroke="scoreColor" stroke-width="5" stroke-linecap="round"
                :stroke-dasharray="miniCircumference" :stroke-dashoffset="miniGaugeOffset"
                transform="rotate(-225 30 30)" class="mini-gauge-progress" />
              <text x="30" y="33" text-anchor="middle" :fill="scoreColor" font-size="14" font-weight="700">{{ salesFlowScoreData.totalScore || 0 }}</text>
            </svg>
          </div>
          <div class="stat-content">
            <span class="stat-label">SalesFlow Score</span>
            <span class="score-label-text" :style="{ color: scoreColor }">{{ scoreLabel }}</span>
          </div>
          <feather-icon icon="ChevronRightIcon" size="16" class="score-chevron" />
        </div>
      </div>

      <!-- Destaque para pedidos em aprovação (só quando houver) -->
      <div v-if="pedidosEmAprovacao.total > 0" class="aprovacao-card">
        <div class="aprovacao-icon"><feather-icon icon="ClockIcon" size="22" /></div>
        <div class="aprovacao-content">
          <span class="aprovacao-title">Pedidos em aprovação</span>
          <span class="aprovacao-desc">
            {{ pedidosEmAprovacao.total }} {{ pedidosEmAprovacao.total === 1 ? 'pedido aguarda' : 'pedidos aguardam' }} aprovação
            <span v-if="pedidosEmAprovacao.valorTotal">· {{ formatCurrency(pedidosEmAprovacao.valorTotal) }}</span>
          </span>
        </div>
        <b-button variant="warning" size="sm" class="aprovacao-btn" @click="mostrarListaAprovacao = !mostrarListaAprovacao">
          <feather-icon :icon="mostrarListaAprovacao ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
          {{ mostrarListaAprovacao ? 'Ocultar' : 'Ver pedidos' }}
        </b-button>
      </div>

      <!-- Lista básica dos pedidos em aprovação -->
      <div v-if="mostrarListaAprovacao && listaAprovacao.length" class="aprovacao-lista">
        <div v-for="p in listaAprovacao" :key="p.pedidoid" class="aprovacao-lista-item">
          <div class="aprovacao-lista-info">
            <span class="aprovacao-lista-pedido">Pedido #{{ p.pedidoid }}</span>
            <span class="aprovacao-lista-cliente">{{ p.clienterazaosocial || 'Cliente não informado' }}</span>
            <span class="aprovacao-lista-meta">
              {{ formatDate(p.pedidodata) }} · {{ formatCurrency(p.pedidovalortotal) }}
              <span v-if="p.tipopedidoid"> · {{ p.tipopedidoid }}</span>
            </span>
          </div>
          <b-button
            variant="outline-warning"
            size="sm"
            @click="abrirDetalhePedido(p)"
          >
            <feather-icon icon="ExternalLinkIcon" size="14" /> Detalhes
          </b-button>
        </div>
      </div>

      <b-modal v-model="showScoreModal" title="SalesFlow Score - Detalhes" size="lg" hide-footer centered modal-class="modal-salesflow-score">
        <sales-flow-score :score-data="salesFlowScoreData" />
      </b-modal>

      <b-row class="mt-2">
        <b-col md="8">
          <b-card class="chart-card">
            <h5 class="card-chart-title">{{ ehRepresentante ? 'Evolução de Doações (R$)' : 'Evolução de Vendas (R$)' }}</h5>
            <vue-apex-charts
              v-if="vendasMensaisOptions.xaxis.categories.length"
              type="area"
              height="320"
              :options="vendasMensaisOptions"
              :series="vendasMensaisSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Pipeline de Pedidos</h5>
            <vue-apex-charts
              v-if="pipelineOptions.labels.length"
              type="donut"
              height="320"
              :options="pipelineOptions"
              :series="pipelineSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
      </b-row>

      <b-row v-if="!ehRepresentante && $store.getters['preferencias/comissoesAtivo']" class="mt-2">
        <b-col md="6">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Comissões por Mês (R$)</h5>
            <vue-apex-charts
              v-if="comissaoMensalOptions.xaxis.categories.length"
              type="bar"
              height="320"
              :options="comissaoMensalOptions"
              :series="comissaoMensalSeries"
            />
            <div v-else class="no-data">Sem dados de comissão</div>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Notas de Comissão por Mês</h5>
            <vue-apex-charts
              v-if="comissaoNotasOptions.xaxis.categories.length"
              type="bar"
              height="320"
              :options="comissaoNotasOptions"
              :series="comissaoNotasSeries"
            />
            <div v-else class="no-data">Sem dados de comissão</div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col md="12">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Meus Clientes</h5>
            <div class="table-responsive">
              <b-table
                v-if="data.meusClientes.length"
                :items="data.meusClientes"
                :fields="clienteFields"
                striped
                hover
                small
                class="mb-0 clientes-table"
                @row-clicked="onClienteClick"
                tbody-tr-class="cursor-pointer"
              >
                <template #cell(valorTotal)="row">
                  {{ formatCurrency(row.item.valorTotal) }}
                </template>
                <template #cell(ultimoPedido)="row">
                  {{ formatDate(row.item.ultimoPedido) }}
                </template>
                <template #cell(clienteinativo)="row">
                  <b-badge :variant="row.item.clienteinativo ? 'light-danger' : 'light-success'">
                    {{ row.item.clienteinativo ? 'Inativo' : 'Ativo' }}
                  </b-badge>
                </template>
              </b-table>
              <div v-else class="no-data">Nenhum cliente encontrado</div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>

    <b-modal
      v-model="modalPedidos.show"
      :title="modalPedidos.clienteNome"
      size="xl"
      hide-footer
      centered
      modal-class="modal-pedidos-cliente"
    >
      <b-overlay :show="modalPedidos.loading" variant="light" blur="2px" min-height="200px">
        <div v-if="!modalPedidos.loading && modalPedidos.data">
          <div class="modal-resumo-grid">
            <div class="modal-resumo-item">
              <feather-icon icon="ShoppingBagIcon" size="18" class="mr-50 text-primary" />
              <div>
                <span class="modal-resumo-label">Total de Pedidos</span>
                <span class="modal-resumo-value">{{ modalPedidos.data.resumo.totalPedidos || 0 }}</span>
              </div>
            </div>
            <div class="modal-resumo-item">
              <feather-icon icon="DollarSignIcon" size="18" class="mr-50 text-success" />
              <div>
                <span class="modal-resumo-label">Valor Total</span>
                <span class="modal-resumo-value">{{ formatCurrency(modalPedidos.data.resumo.valorTotal) }}</span>
              </div>
            </div>
            <div class="modal-resumo-item">
              <feather-icon icon="TrendingUpIcon" size="18" class="mr-50 text-warning" />
              <div>
                <span class="modal-resumo-label">Ticket Médio</span>
                <span class="modal-resumo-value">{{ formatCurrency(modalPedidos.data.resumo.ticketMedio) }}</span>
              </div>
            </div>
            <div class="modal-resumo-item">
              <feather-icon icon="CalendarIcon" size="18" class="mr-50 text-info" />
              <div>
                <span class="modal-resumo-label">Primeiro Pedido</span>
                <span class="modal-resumo-value">{{ formatDate(modalPedidos.data.resumo.primeiroPedido) }}</span>
              </div>
            </div>
            <div class="modal-resumo-item">
              <feather-icon icon="ClockIcon" size="18" class="mr-50 text-info" />
              <div>
                <span class="modal-resumo-label">Último Pedido</span>
                <span class="modal-resumo-value">{{ formatDate(modalPedidos.data.resumo.ultimoPedido) }}</span>
              </div>
            </div>
          </div>

          <div class="modal-pedidos-info mt-1 mb-1">
            <span class="text-muted">CNPJ: <strong>{{ modalPedidos.clienteCnpj }}</strong></span>
          </div>

          <div class="table-responsive">
            <b-table
              v-if="modalPedidos.data.pedidos.length"
              :items="modalPedidos.data.pedidos"
              :fields="pedidoFields.filter(field => field.key !== 'pedidodataentrega' || $store.getters['preferencias/exibirDataPrevistaEntrega'] !== false)"
              striped
              hover
              small
              class="mb-0"
              :per-page="modalPedidos.perPage"
              :current-page="modalPedidos.currentPage"
            >
              <template #cell(pedidodata)="row">
                {{ formatDate(row.item.pedidodata) }}
              </template>
              <template #cell(pedidovalortotal)="row">
                {{ formatCurrency(row.item.pedidovalortotal) }}
              </template>
              <template #cell(pedidodataentrega)="row">
                {{ formatDate(row.item.pedidodataentrega) }}
              </template>
              <template #cell(pedidostatusworkflow)="row">
                <b-badge :variant="getStatusVariant(row.item.pedidostatusworkflow)">
                  {{ row.item.pedidostatusworkflow || 'Sem Status' }}
                </b-badge>
              </template>
            </b-table>
            <div v-else class="no-data py-2">Nenhum pedido encontrado para este cliente</div>
          </div>

          <div v-if="modalPedidos.data.pedidos.length > modalPedidos.perPage" class="d-flex justify-content-between align-items-center mt-1">
            <span class="text-muted" style="font-size: 0.85rem;">
              Mostrando {{ ((modalPedidos.currentPage - 1) * modalPedidos.perPage) + 1 }}
              a {{ Math.min(modalPedidos.currentPage * modalPedidos.perPage, modalPedidos.data.pedidos.length) }}
              de {{ modalPedidos.data.pedidos.length }} pedidos
            </span>
            <b-pagination
              v-model="modalPedidos.currentPage"
              :total-rows="modalPedidos.data.pedidos.length"
              :per-page="modalPedidos.perPage"
              size="sm"
              class="mb-0"
            />
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import axios from '@/libs/axios';
import { BOverlay, BRow, BCol, BCard, BButton, BFormDatepicker, BTable, BBadge, BModal, BPagination } from 'bootstrap-vue';
import SalesFlowScore from './components/SalesFlowScore.vue';
import GamificationDashboard from '@/views/gamification/GamificationDashboard.vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'DashboardEquipeVendas',
  components: { VueApexCharts, BOverlay, BRow, BCol, BCard, BButton, BFormDatepicker, BTable, BBadge, BModal, BPagination, SalesFlowScore, GamificationDashboard, ToastificationContent },
  data() {
    return {
      activeTab: 'vendas',
      isLoading: false,
      filters: { dataInicio: '', dataFim: '' },
      salesFlowScoreData: { totalScore: 0, dimensions: {}, alerts: [], rawMetrics: {} },
      showScoreModal: false,
      scoreHoverTimer: null,
      mostrarListaAprovacao: false,
      data: {
        meusPedidos: [],
        pedidosPorStatus: [],
        comissaoResumo: [],
        comissaoNotas: [],
        meusClientes: [],
        pedidosEmAprovacaoLista: [],
        resumo: {},
        comissaoTotal: {},
      },
      clienteFields: [
        { key: 'cliente', label: 'Razão Social', sortable: true },
        { key: 'cnpj', label: 'CNPJ' },
        { key: 'totalPedidos', label: 'Pedidos', sortable: true },
        { key: 'valorTotal', label: 'Valor Total', sortable: true },
        { key: 'ultimoPedido', label: 'Último Pedido', sortable: true },
        { key: 'clienteinativo', label: 'Status' },
      ],
      pedidoFields: [
        { key: 'pedidoid', label: 'Pedido', sortable: true },
        { key: 'pedidonumerocliente', label: 'Nº Cliente' },
        { key: 'pedidodata', label: 'Data', sortable: true },
        { key: 'pedidovalortotal', label: 'Valor', sortable: true },
        { key: 'condicaopagamentoid', label: 'Cond. Pgto' },
        { key: 'pedidodataentrega', label: 'Entrega' },
        { key: 'notafiscalnumero', label: 'Nota Fiscal' },
        { key: 'pedidostatusworkflow', label: 'Status' },
      ],
      modalPedidos: {
        show: false,
        loading: false,
        clienteNome: '',
        clienteCnpj: '',
        clienteId: null,
        data: null,
        currentPage: 1,
        perPage: 10,
      },
    };
  },
  computed: {
    // Saudação: primeiro nome do usuário logado (userData plano ou aninhado)
    nomeUsuarioLogado() {
      try {
        const ud = JSON.parse(localStorage.getItem('userData') || 'null');
        const nome = ud?.nome || ud?.userData?.nome || '';
        return String(nome).trim().split(/\s+/)[0] || '';
      } catch (e) {
        return '';
      }
    },
    ehRepresentante() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
        return ['representante', 'supervisor'].includes((perfilLogado.perfil_acesso_nome || '').trim().toLowerCase());
      } catch (e) {
        return false;
      }
    },
    // CRM do tenant (controla a tab de Gamificação)
    crmAtivo() {
      return this.$store.getters['preferencias/crmAtivo'];
    },
    // Agrega pedidos com status EM APROVAÇÃO a partir de pedidosPorStatus
    pedidosEmAprovacao() {
      const lista = this.data.pedidosPorStatus || [];
      let total = 0;
      let valorTotal = 0;
      lista.forEach((s) => {
        const status = String(s.status || '').trim().toUpperCase();
        if (status === 'EM APROVAÇÃO' || status === 'EM APROVACAO') {
          total += Number(s.total) || 0;
          valorTotal += Number(s.valorTotal) || 0;
        }
      });
      return { total, valorTotal };
    },
    listaAprovacao() {
      return this.data.pedidosEmAprovacaoLista || [];
    },
    chartTheme() {
      return this.$store?.state?.appConfig?.layout?.skin === 'dark' ? 'dark' : 'light';
    },
    miniCircumference() {
      return 2 * Math.PI * 24 * 0.75;
    },
    miniGaugeOffset() {
      const pct = (this.salesFlowScoreData.totalScore || 0) / 100;
      return this.miniCircumference * (1 - pct);
    },
    scoreColor() {
      const s = this.salesFlowScoreData.totalScore || 0;
      if (s >= 70) return '#16a34a';
      if (s >= 40) return '#f59e0b';
      return '#dc2626';
    },
    scoreLabel() {
      const s = this.salesFlowScoreData.totalScore || 0;
      if (s >= 80) return 'Excelente';
      if (s >= 70) return 'Bom';
      if (s >= 50) return 'Regular';
      if (s >= 30) return 'Atenção';
      return 'Crítico';
    },
    vendasMensaisSeries() {
      return [{ name: this.ehRepresentante ? 'Doações (R$)' : 'Vendas (R$)', data: this.data.meusPedidos.map(p => Number(p.valorTotal) || 0) }];
    },
    vendasMensaisOptions() {
      return {
        chart: { type: 'area', toolbar: { show: false } },
        xaxis: { categories: this.data.meusPedidos.map(p => p.mes || '') },
        colors: ['#2563eb'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 } },
        dataLabels: { enabled: false },
        yaxis: { labels: { formatter: val => this.formatCurrencyShort(val) } },
        tooltip: { theme: this.chartTheme, y: { formatter: val => this.formatCurrency(val) } },
      };
    },
    pipelineSeries() {
      return this.data.pedidosPorStatus.map(s => Number(s.total) || 0);
    },
    pipelineOptions() {
      return {
        chart: { type: 'donut' },
        labels: this.data.pedidosPorStatus.map(s => s.status || 'N/A'),
        colors: ['#16a34a', '#2563eb', '#f59e0b', '#dc2626', '#0ea5e9', '#b8c2cc'],
        legend: { position: 'bottom' },
        tooltip: { theme: this.chartTheme },
      };
    },
    comissaoMensalSeries() {
      return [
        { name: 'Base Comissão', data: this.data.comissaoResumo.map(c => Number(c.baseComissao) || 0) },
        { name: 'Total Comissão', data: this.data.comissaoResumo.map(c => Number(c.totalComissao) || 0) },
      ];
    },
    comissaoMensalOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        xaxis: { categories: this.data.comissaoResumo.map(c => `${c.mes_comis}/${c.ano_comis}`) },
        colors: ['#0ea5e9', '#16a34a'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        dataLabels: { enabled: false },
        yaxis: { labels: { formatter: val => this.formatCurrencyShort(val) } },
        tooltip: { theme: this.chartTheme, y: { formatter: val => this.formatCurrency(val) } },
      };
    },
    comissaoNotasSeries() {
      return [
        { name: 'Valor Notas', data: this.data.comissaoNotas.map(c => Number(c.valorNotas) || 0) },
        { name: 'Valor Comissão', data: this.data.comissaoNotas.map(c => Number(c.valorComissao) || 0) },
      ];
    },
    comissaoNotasOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false }, stacked: false },
        xaxis: { categories: this.data.comissaoNotas.map(c => `${c.mes_comis}/${c.ano_comis}`) },
        colors: ['#f59e0b', '#2563eb'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        dataLabels: { enabled: false },
        yaxis: { labels: { formatter: val => this.formatCurrencyShort(val) } },
        tooltip: { theme: this.chartTheme, y: { formatter: val => this.formatCurrency(val) } },
      };
    },
  },
  watch: {
    // Se o CRM for desativado e a tab ativa for gamificação, volta para pedidos
    crmAtivo(ativo) {
      if (!ativo && this.activeTab === 'gamificacao') {
        this.activeTab = 'vendas';
      }
    },
  },
  async created() {
    // Garante o estado inicial correto caso o CRM esteja desativado
    if (!this.crmAtivo && this.activeTab === 'gamificacao') {
      this.activeTab = 'vendas';
    }
    await this.loadData();
  },
  methods: {
    formatCurrency(value) {
      return 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatCurrencyShort(value) {
      const n = Number(value || 0);
      if (n >= 1000000) return 'R$ ' + (n / 1000000).toFixed(1) + 'M';
      if (n >= 1000) return 'R$ ' + (n / 1000).toFixed(1) + 'K';
      return 'R$ ' + n.toFixed(0);
    },
    formatDate(value) {
      if (!value) return '-';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('pt-BR');
    },
    getStatusVariant(status) {
      if (!status) return 'light-secondary';
      const s = status.toLowerCase();
      if (s.includes('aprovado') || s.includes('faturado') || s.includes('conclu')) return 'light-success';
      if (s.includes('cancel') || s.includes('rejeitado') || s.includes('recusado')) return 'light-danger';
      if (s.includes('pendente') || s.includes('aguard') || s.includes('analis')) return 'light-warning';
      if (s.includes('aberto') || s.includes('digitação') || s.includes('rascunho')) return 'light-info';
      return 'light-secondary';
    },
    getRepresentanteId() {
      try {
        const representanteData = JSON.parse(localStorage.getItem('representanteData'));
        if (representanteData?.representanteid) return representanteData.representanteid;
        const clienteData = JSON.parse(localStorage.getItem('clienteData'));
        if (clienteData?.representante?.representanteid) return clienteData.representante.representanteid;
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (userData?.representanteid) return userData.representanteid;
        return '';
      } catch { return ''; }
    },
    async loadData() {
      this.isLoading = true;
      try {
        const params = {};
        const repId = this.getRepresentanteId();
        if (repId) params.representanteid = repId;
        if (this.filters.dataInicio) params.dataInicio = this.filters.dataInicio;
        if (this.filters.dataFim) params.dataFim = this.filters.dataFim;
        const [dashRes, scoreRes] = await Promise.all([
          axios.get('/dashboard/equipe-vendas', { params }),
          axios.get('/dashboard/salesflow-score', { params: { representanteid: repId } }).catch(() => ({ data: { totalScore: 0, dimensions: {}, alerts: [], rawMetrics: {} } })),
        ]);
        this.data = dashRes.data;
        this.salesFlowScoreData = scoreRes.data;
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar dashboard',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
    // Navega para a listagem de pedidos (já abre filtrada em doação para o rep)
    verPedidosAprovacao() {
      this.$router.push({ name: 'pedidos-vendas' });
    },
    abrirDetalhePedido(p) {
      this.$router.push({
        name: 'pedidos-vendas-detalhes',
        params: { pedidoid: p.pedidoid, clienteid: p.clienteid },
      });
    },
    startScoreHover() {
      this.scoreHoverTimer = setTimeout(() => {
        this.showScoreModal = true;
      }, 2000);
    },
    cancelScoreHover() {
      if (this.scoreHoverTimer) {
        clearTimeout(this.scoreHoverTimer);
        this.scoreHoverTimer = null;
      }
    },
    async onClienteClick(item) {
      this.modalPedidos.show = true;
      this.modalPedidos.loading = true;
      this.modalPedidos.clienteNome = item.cliente || 'Cliente';
      this.modalPedidos.clienteCnpj = item.cnpj || '';
      this.modalPedidos.clienteId = item.clienteid;
      this.modalPedidos.data = null;
      this.modalPedidos.currentPage = 1;
      try {
        const params = { clienteid: item.clienteid };
        const repId = this.getRepresentanteId();
        if (repId) params.representanteid = repId;
        const { data } = await axios.get('/dashboard/pedidos-cliente', { params });
        this.modalPedidos.data = data;
      } catch (error) {
        console.error('Erro ao carregar pedidos do cliente:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar pedidos do cliente',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
        this.modalPedidos.show = false;
      } finally {
        this.modalPedidos.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* 
  B2B Premium Palette:
  1: #01094e (Deep Navy)
  2: #0575b5 (Ocean Blue)
  3: #00b7d6 (Vibrant Cyan)
  4: #8fdeea (Pale Sky Blue)
  5: #d6f4fa (Ice Blue)
*/

.dashboard-vendas { padding: 1rem; --info-divider-color: rgba(143, 222, 234, 0.2); }

/* Header */
.page-header { margin-bottom: 2rem; }
.header-top { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem; }
.page-greeting {
  font-size: 1rem;
  font-weight: 600;
  color: #6e6b7b;
  margin-bottom: 0.15rem;
}
.page-title { 
  font-size: 1.75rem; 
  font-weight: 700; 
  color: #01094e; 
  margin-bottom: 0.25rem; 
  letter-spacing: -0.01em;
}
.page-subtitle { color: #0575b5; font-size: 1rem; font-weight: 400; opacity: 0.8; }

/* Filters Inline */
.filters-inline { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.filter-date { max-width: 160px; border-radius: 8px; border: 1px solid rgba(143, 222, 234, 0.6); }

/* Tabs */
.dashboard-tabs-header {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.75rem;
  border-bottom: 2px solid rgba(143, 222, 234, 0.3);
  padding-bottom: 0px;
}
.dashboard-tab-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: #0575b5;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: all 0.3s ease;
  opacity: 0.7;
}
.dashboard-tab-btn:hover {
  color: #01094e;
  opacity: 1;
}
.dashboard-tab-btn.active {
  color: #01094e;
  border-bottom-color: #00b7d6;
  opacity: 1;
}

/* Stat Grid */
.stats-grid { 
  display: grid; 
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); 
  gap: 1.25rem; 
  margin-bottom: 1.5rem; 
}

/* Glassmorphic Stat Cards */
.stat-card { 
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(143, 222, 234, 0.4);
  border-radius: 12px; 
  padding: 1.5rem; 
  display: flex; 
  align-items: center; 
  gap: 1.25rem; 
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); 
  position: relative; 
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s ease;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
  border-color: rgba(0, 183, 214, 0.4);
}

/* Card destaque: pedidos em aprovação */
.aprovacao-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.4);
  border-left: 4px solid #f59e0b;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
}
.aprovacao-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.1));
  color: #b45309;
}
.aprovacao-content { flex: 1; min-width: 0; }
.aprovacao-title {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  color: #01094e;
}
.aprovacao-desc {
  display: block;
  font-size: 0.9rem;
  color: #b45309;
  font-weight: 500;
}
.aprovacao-btn { flex-shrink: 0; }

/* Lista expandida dos pedidos em aprovação */
.aprovacao-lista {
  margin: -0.75rem 0 1.5rem 0;
  border: 1px solid rgba(245, 158, 11, 0.35);
  border-top: none;
  border-radius: 0 0 12px 12px;
  overflow: hidden;
}
.aprovacao-lista-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  background: rgba(245, 158, 11, 0.04);
}
.aprovacao-lista-item + .aprovacao-lista-item {
  border-top: 1px solid rgba(245, 158, 11, 0.2);
}
.aprovacao-lista-info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.aprovacao-lista-pedido { font-weight: 700; font-size: 0.9rem; color: #01094e; }
.aprovacao-lista-cliente {
  font-size: 0.85rem;
  color: #5e5873;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aprovacao-lista-meta { font-size: 0.8rem; color: #b45309; font-weight: 500; }

/* Base Stat Icon */
.stat-icon { 
  width: 56px; 
  height: 56px; 
  border-radius: 12px; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  background: linear-gradient(135deg, rgba(5, 117, 181, 0.1), rgba(0, 183, 214, 0.1));
  color: #0575b5;
  border: 1px solid rgba(0, 183, 214, 0.15);
  transition: all 0.3s ease;
}
.stat-card:hover .stat-icon {
  background: linear-gradient(135deg, #0575b5, #00b7d6);
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 183, 214, 0.4);
  border-color: transparent;
}

/* Score Specific Overrides */
.stat-score { cursor: pointer; border-left: 4px solid transparent; }
.score-gauge-mini { flex-shrink: 0; width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; }
.score-label-text { display: block; font-size: 0.95rem; font-weight: 700; margin-top: 0.1rem; }
.score-chevron { position: absolute; right: 1rem; top: 50%; transform: translateY(-50%); color: #0575b5; opacity: 0; transition: opacity 0.3s, right 0.3s; }
.stat-score:hover .score-chevron { opacity: 1; right: 0.75rem; }
.mini-gauge-progress { transition: stroke-dashoffset 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94); }

/* Typography inside Stats */
.stat-label { 
  display: block; 
  font-size: 0.9rem; 
  font-weight: 500;
  color: #0575b5; 
  opacity: 0.8;
  margin-bottom: 0.25rem;
}
.stat-value { 
  display: block; 
  font-size: 1.75rem; 
  font-weight: 700; 
  color: #01094e; 
  line-height: 1.1;
}

/* Chart and Info Cards */
.chart-card { 
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(143, 222, 234, 0.25);
  border-radius: 12px; 
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); 
  transition: box-shadow 0.3s ease;
}
.chart-card:hover {
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
}
.card-chart-title { 
  font-size: 1.1rem; 
  font-weight: 700; 
  color: #01094e; 
  margin-bottom: 1.25rem; 
  letter-spacing: -0.01em;
}
.no-data { 
  text-align: center; 
  padding: 3rem; 
  color: #8fdeea; 
  font-size: 1rem; 
  font-weight: 500;
}

/* Data Tables */
.clientes-table >>> tbody tr { cursor: pointer; transition: background-color 0.2s; }
.clientes-table >>> tbody tr:hover { background-color: rgba(0, 183, 214, 0.06) !important; }

/* Modal Content */
.modal-resumo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  padding: 1rem;
  background: rgba(214, 244, 250, 0.3);
  border: 1px solid rgba(143, 222, 234, 0.4);
  border-radius: 10px;
  margin-bottom: 1rem;
}
.modal-resumo-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.modal-resumo-label {
  display: block;
  font-size: 0.8rem;
  color: #0575b5;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 0.2rem;
}
.modal-resumo-value {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: #01094e;
}
.modal-pedidos-info { font-size: 0.95rem; color: #01094e; }

/* ================= Dark Layout Overrides ================= */
body.dark-layout .dashboard-vendas .page-title {
  color: #d6f4fa;
}
body.dark-layout .dashboard-vendas .page-subtitle {
  color: #8fdeea;
}
body.dark-layout .dashboard-vendas .dashboard-tabs-header {
  border-bottom-color: rgba(143, 222, 234, 0.15);
}
body.dark-layout .dashboard-vendas .dashboard-tab-btn {
  color: #8fdeea;
}
body.dark-layout .dashboard-vendas .dashboard-tab-btn:hover {
  color: #d6f4fa;
}
body.dark-layout .dashboard-vendas .dashboard-tab-btn.active {
  color: #00b7d6;
  border-bottom-color: #00b7d6;
}
body.dark-layout .dashboard-vendas .stat-card,
body.dark-layout .dashboard-vendas .chart-card {
  background: rgba(1, 9, 78, 0.6);
  border-color: rgba(143, 222, 234, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
body.dark-layout .dashboard-vendas .modal-resumo-grid {
  background: rgba(1, 9, 78, 0.4);
  border-color: rgba(143, 222, 234, 0.15);
  box-shadow: inset 0 2px 10px rgba(0,0,0,0.2);
}
body.dark-layout .dashboard-vendas .stat-card:hover,
body.dark-layout .dashboard-vendas .chart-card:hover {
  background: rgba(1, 9, 78, 0.8);
  border-color: rgba(0, 183, 214, 0.3);
  box-shadow: 0 12px 40px rgba(0, 183, 214, 0.15);
}
body.dark-layout .dashboard-vendas .stat-label,
body.dark-layout .dashboard-vendas .modal-resumo-label {
  color: #8fdeea;
}
body.dark-layout .dashboard-vendas .stat-value,
body.dark-layout .dashboard-vendas .card-chart-title,
body.dark-layout .dashboard-vendas .modal-resumo-value {
  color: #d6f4fa;
}
body.dark-layout .dashboard-vendas .stat-icon {
  background: rgba(0, 183, 214, 0.1);
  color: #00b7d6;
  border-color: rgba(0, 183, 214, 0.2);
}
body.dark-layout .dashboard-vendas .stat-card:hover .stat-icon {
  background: linear-gradient(135deg, #0575b5, #00b7d6);
  color: #fff;
  box-shadow: 0 0 15px rgba(0, 183, 214, 0.5);
  border-color: transparent;
}
body.dark-layout .dashboard-vendas .aprovacao-title,
body.dark-layout .dashboard-vendas .aprovacao-lista-pedido {
  color: #e2e8f0;
}
body.dark-layout .dashboard-vendas .aprovacao-lista-cliente {
  color: #b4b7bd;
}
body.dark-layout .dashboard-vendas .aprovacao-lista-item {
  background: rgba(245, 158, 11, 0.06);
}
body.dark-layout .dashboard-vendas .no-data {
  color: #0575b5;
}
body.dark-layout .dashboard-vendas .score-chevron {
  color: #8fdeea;
}
</style>
